import {useInfiniteQuery} from '@tanstack/react-query';
import {fetchRecords} from '../services/BindingDriftReconciliationService';
import {useState} from 'react';

// FNP-585: read-only module. No delete/create/update mutations — the binding-drift
// sweep (FNP-502) owns the lifecycle. This hook is list-fetch only. There are no
// filter variants (the sweep scans the whole platform), so unlike billable-events
// there is no module-scoped filter store.
export const useBindingDriftReports = () => {
    const [limit] = useState(10);

    const sort = {column: 'runStartedAt', direction: 'desc' as const};

    // INFINITE QUERY — newest run first (the recordType-runStartedAt GSI is already
    // ordered DESC server-side; sort is carried for parity with the peer modules).
    const reportsQuery = useInfiniteQuery({
        queryKey: ['bindingDriftReports', limit, sort.column, sort.direction],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit, sort, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const fetchRecordsAction = () => {
        if (reportsQuery.hasNextPage) {
            reportsQuery.fetchNextPage();
        }
    };

    const reload = () => {
        reportsQuery.refetch();
    };

    // The service layer guarantees page.items is a real array even on a GraphQL-level
    // error; filter(Boolean) keeps an undefined row out of the table as defense-in-depth.
    const allItems = (reportsQuery.data?.pages?.flatMap(page => page?.items ?? []) || []).filter(Boolean);

    return {
        items: allItems,
        visibleItems: allItems,
        loading: reportsQuery.isLoading,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: reportsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reload,
    };
};
